import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/boot/axios'
import { localDb } from '@/services'
import { useLocationStore } from './location'
import { useAuthStore } from './auth'

export const useRegisterStore = defineStore('register', () => {
  // State
  const registers = ref([])
  const loading = ref(false)
  const currentRegister = ref(null)
  const locationStore = useLocationStore()
  const authStore = useAuthStore()

  // Terminal-related state
  const registersWithTerminals = ref([])
  const terminalStatusLoading = ref(false)
  let terminalOpsInflightCount = 0
  const syncTerminalOpsLoading = () => {
    terminalStatusLoading.value = terminalOpsInflightCount > 0
  }

  // Getters
  const getRegistersByLocation = computed(() => {
    const currentLocationId = locationStore.getCurrentLocation?.id
    if (!currentLocationId) return []
    return registers.value.filter(register => register.location_id === currentLocationId)
  })

  const getAvailableRegisters = computed(() => {
    return registers.value.filter(register => register.is_available)
  })

  const getOpenRegisters = computed(() => {
    return registers.value.filter(register => register.is_open)
  })

  const getRegisterById = computed(() => {
    return (id) => registers.value.find(register => register.id === id)
  })

  // Terminal-related computed properties
  const getRegistersWithTerminals = computed(() => {
    return registersWithTerminals.value
  })

  const getRegistersReadyForCardPayments = computed(() => {
    return registersWithTerminals.value.filter(register => register.terminal_healthy)
  })

  const getCurrentRegisterTerminalStatus = computed(() => {
    if (!currentRegister.value) return null
    const registerWithTerminal = registersWithTerminals.value.find(r => r.register.id === currentRegister.value.id)
    return registerWithTerminal?.terminal_status || null
  })

  const getCurrentRegisterCanProcessCards = computed(() => {
    if (!currentRegister.value) return false
    const registerWithTerminal = registersWithTerminals.value.find(r => r.register.id === currentRegister.value.id)
    return registerWithTerminal?.terminal_healthy || false
  })

  const isCurrentRegisterOpen = computed(() => {
    return currentRegister.value && currentRegister.value.is_open
  })

  // Actions
  const ensureOnline = (actionLabel) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new Error(`Offline mode: ${actionLabel} requires an internet connection.`)
    }
  }

  const persistOverviewCache = async (locationId) => {
    try {
      await localDb.cacheSetRegisterOverview(locationId, {
        locationId,
        fetchedAt: Date.now(),
        registers: registers.value || [],
        currentRegister: currentRegister.value || null,
        registersWithTerminals: registersWithTerminals.value || []
      })
    } catch (e) {
      console.warn('Failed to persist register overview to local DB:', e)
    }
  }

  const hydrateOverviewFromCache = async ({ locationId } = {}) => {
    try {
      const locId = locationId ?? locationStore.getCurrentLocation?.id
      if (!locId) return false
      const cached = await localDb.cacheGetRegisterOverview(locId)
      if (!cached) return false

      registers.value = cached.registers || []
      currentRegister.value = cached.currentRegister || null
      registersWithTerminals.value = cached.registersWithTerminals || []

      const ts = cached.fetchedAt || Date.now()
      overviewLastFetchedAt = ts
      registersLastFetchedAt = ts
      currentRegisterLastFetchedAt = ts
      rwtLastFetchedAt = ts
      return true
    } catch (e) {
      console.warn('Failed to hydrate register overview from local DB:', e)
      return false
    }
  }

  let loadOverviewInflight = null
  let overviewLastFetchedAt = 0
  const loadOverview = async ({ force = false } = {}) => {
    const locationId = locationStore.getCurrentLocation?.id
    if (!locationId) {
      return null
    }

    const isStaleSession = () =>
      !authStore.isAuthenticated ||
      locationStore.getCurrentLocation?.id !== locationId

    if (!force && loadOverviewInflight) return loadOverviewInflight
    const now = Date.now()
    if (!force && now - overviewLastFetchedAt < 30000) {
      // recent enough; return current state snapshot
      return {
        registers: registers.value,
        current_register: currentRegister.value,
        registers_with_terminals: registersWithTerminals.value
      }
    }

    loading.value = true
    try {
      loadOverviewInflight = api.get('/registers/overview')
      const response = await loadOverviewInflight
      if (isStaleSession()) {
        return null
      }
      if (response.data?.success) {
        const data = response.data.data || {}

        // New streamlined shape: data.registers with embedded terminal, and current_register_id
        if (Array.isArray(data.registers)) {
          registers.value = data.registers
          // Derive registersWithTerminals to keep backwards compatibility for consumers
          registersWithTerminals.value = data.registers.map(r => ({
            register: {
              id: r.id,
              name: r.name,
              description: r.description,
              status: r.status,
              location_id: r.location?.id ?? locationId
            },
            terminal_healthy: r.terminal?.healthy || false,
            terminal: r.terminal ? {
              id: r.terminal.id,
              external_id: r.terminal.external_id,
              label: r.terminal.label,
              status: r.terminal.status,
              capabilities: r.terminal.capabilities
            } : null
          }))
          // Set current register from id if provided
          if (data.current_register_id) {
            currentRegister.value = registers.value.find(r => r.id === data.current_register_id) || null
          } else {
            currentRegister.value = null
          }
        } else {
          // Backward compatibility with previous aggregated shape
          registers.value = Array.isArray(data.registers) ? data.registers : []
          currentRegister.value = data.current_register || null
          registersWithTerminals.value = Array.isArray(data.registers_with_terminals) ? data.registers_with_terminals : []
        }
        overviewLastFetchedAt = Date.now()
        registersLastFetchedAt = overviewLastFetchedAt
        currentRegisterLastFetchedAt = overviewLastFetchedAt
        rwtLastFetchedAt = overviewLastFetchedAt
        if (!isStaleSession()) {
          await persistOverviewCache(locationId)
        }
        return data
      }
      throw new Error(response.data?.message || 'Failed to load register overview')
    } catch (error) {
      if (isStaleSession()) {
        return null
      }
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false
      if (offline || !error?.response) {
        const ok = await hydrateOverviewFromCache({ locationId })
        if (ok) {
          return {
            registers: registers.value,
            current_register: currentRegister.value,
            registers_with_terminals: registersWithTerminals.value
          }
        }
      }
      console.error('Error loading register overview:', error)
      throw error
    } finally {
      loading.value = false
      loadOverviewInflight = null
    }
  }
  let loadRegistersInflight = null
  let registersLastFetchedAt = 0
  const loadRegisters = async ({ force = false } = {}) => {
    if (!force && loadRegistersInflight) return loadRegistersInflight
    const now = Date.now()
    if (!force && now - registersLastFetchedAt < 60000 && registers.value.length > 0) {
      return registers.value
    }
    loading.value = true
    try {
      loadRegistersInflight = (async () => {
      const currentLocation = locationStore.getCurrentLocation
      if (!currentLocation) {
        throw new Error('No location selected')
      }

      const response = await api.get(`/locations/${currentLocation.id}/registers`)
      if (response.data.success) {
        registers.value = response.data.data.registers
          registersLastFetchedAt = Date.now()
          return registers.value
      } else {
        throw new Error(response.data.message || 'Failed to load registers')
      }
      })()
      return await loadRegistersInflight
    } catch (error) {
      console.error('Error loading registers:', error)
      throw error
    } finally {
      loading.value = false
      loadRegistersInflight = null
    }
  }

  /**
   * Load the current user's active register
   */
  let loadCurrentRegisterInflight = null
  let currentRegisterLastFetchedAt = 0

  const hydrateCurrentRegisterFromBootstrap = (register) => {
    currentRegister.value = register ?? null
    currentRegisterLastFetchedAt = Date.now()
    return currentRegister.value
  }

  const loadCurrentUserRegister = async ({ force = false, includeAllLocations = false } = {}) => {
    if (!force && loadCurrentRegisterInflight) return loadCurrentRegisterInflight
    const now = Date.now()
    if (!force && !includeAllLocations && now - currentRegisterLastFetchedAt < 60000 && currentRegister.value) {
      return currentRegister.value
    }
    try {
      loadCurrentRegisterInflight = api.get('/registers/current', {
        params: includeAllLocations ? { include_all_locations: 1 } : undefined
      })
      const response = await loadCurrentRegisterInflight
      if (response.data.success) {
        if (!includeAllLocations) {
          currentRegister.value = response.data.data
        }
        if (!includeAllLocations) {
          currentRegisterLastFetchedAt = Date.now()
        }
        return response.data.data
      } else {
        if (!includeAllLocations) {
          currentRegister.value = null
        }
        return null
      }
    } catch (error) {
      if (!authStore.isAuthenticated || error.response?.status === 401) {
        if (!includeAllLocations) {
          currentRegister.value = null
        }
        return null
      }
      console.error('Failed to fetch current user register:', error)
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false
      if (offline || !error?.response) {
        const ok = await hydrateOverviewFromCache({ locationId: locationStore.getCurrentLocation?.id })
        if (ok) return currentRegister.value
      }
      if (!includeAllLocations) {
        currentRegister.value = null
      }
      return null
    } finally {
      loadCurrentRegisterInflight = null
    }
  }

  const createRegister = async (registerData) => {
    try {
      const currentLocation = locationStore.getCurrentLocation
      if (!currentLocation) {
        throw new Error('No location selected')
      }

      const response = await api.post(`/locations/${currentLocation.id}/registers`, registerData)
      if (response.data.success) {
        const newRegister = response.data.data
        registers.value.push(newRegister)
        return newRegister
      } else {
        throw new Error(response.data.message || 'Failed to create register')
      }
    } catch (error) {
      console.error('Error creating register:', error)
      throw error
    }
  }

  const updateRegister = async (registerId, updateData) => {
    try {
      const response = await api.put(`/registers/${registerId}`, updateData)
      if (response.data.success) {
        const updatedRegister = response.data.data
        const index = registers.value.findIndex(r => r.id === registerId)
        if (index !== -1) {
          registers.value[index] = updatedRegister
        }
        return updatedRegister
      } else {
        throw new Error(response.data.message || 'Failed to update register')
      }
    } catch (error) {
      console.error('Error updating register:', error)
      throw error
    }
  }

  const deleteRegister = async (registerId) => {
    try {
      const response = await api.delete(`/registers/${registerId}`)
      if (response.data.success) {
        const index = registers.value.findIndex(r => r.id === registerId)
        if (index !== -1) {
          registers.value.splice(index, 1)
        }
        return true
      } else {
        throw new Error(response.data.message || 'Failed to delete register')
      }
    } catch (error) {
      console.error('Error deleting register:', error)
      throw error
    }
  }

  const openRegister = async (registerId, openingCash = {}) => {
    try {
      ensureOnline('opening a register')

      const response = await api.post(`/registers/${registerId}/operations/open`, {
        opening_cash: openingCash
      })

      if (response.data.success) {
        // Refresh registers to get updated status
        await loadRegisters()
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to open register')
      }
    } catch (error) {
      console.error('Error opening register:', error)
      throw error
    }
  }

  const closeRegister = async (registerId, closingCash = {}, notes = '') => {
    try {
      ensureOnline('closing a register')
      const response = await api.post(`/registers/${registerId}/operations/close`, {
        closing_cash: closingCash,
        notes: notes
      })

      if (response.data.success) {
        // Refresh registers to get updated status
        await loadRegisters()
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to close register')
      }
    } catch (error) {
      console.error('Error closing register:', error)
      throw error
    }
  }

  const assignUserToRegister = async (registerId, userId) => {
    try {
      const response = await api.post(`/registers/${registerId}/operations/assign`, {
        user_id: userId
      })

      if (response.data.success) {
        await loadRegisters()
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to assign user to register')
      }
    } catch (error) {
      console.error('Error assigning user to register:', error)
      throw error
    }
  }

  const unassignUserFromRegister = async (registerId, reason = '') => {
    try {
      const response = await api.post(`/registers/${registerId}/operations/unassign`, {
        reason: reason
      })

      if (response.data.success) {
        await loadRegisters()
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to unassign user from register')
      }
    } catch (error) {
      console.error('Error unassigning user from register:', error)
      throw error
    }
  }

  const getRegisterStatus = async (registerId) => {
    try {
      const response = await api.get(`/registers/${registerId}/health`)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to get register status')
      }
    } catch (error) {
      console.error('Error getting register status:', error)
      throw error
    }
  }

  const getPerformanceMetrics = async (registerId, { force = false } = {}) => {
    try {
      const response = await api.get(`/registers/${registerId}/performance`, {
        params: force ? { force: 1 } : undefined
      })
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to get performance metrics')
      }
    } catch (error) {
      console.error('Error getting performance metrics:', error)
      throw error
    }
  }

  const getCashStatus = async (registerId) => {
    try {
      const response = await api.get(`/registers/${registerId}/operations/cash-status`)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to get cash status')
      }
    } catch (error) {
      console.error('Error getting cash status:', error)
      throw error
    }
  }

  const recordCashAdjustment = async (registerId, adjustment, reason) => {
    try {
      const response = await api.post(`/registers/${registerId}/operations/cash-adjustment`, {
        adjustment: adjustment,
        reason: reason
      })

      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to record cash adjustment')
      }
    } catch (error) {
      console.error('Error recording cash adjustment:', error)
      throw error
    }
  }

  const getCashBreakdown = async (registerId, type) => {
    try {
      const response = await api.get(`/registers/${registerId}/operations/cash-breakdown`, {
        params: { type }
      })

      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to get cash breakdown')
      }
    } catch (error) {
      console.error('Error getting cash breakdown:', error)
      throw error
    }
  }

  const getAuditLog = async (registerId, filters = {}) => {
    try {
      const response = await api.get(`/registers/${registerId}/data/audit-log`, {
        params: filters
      })

      console.log(response)

      if (response.data.success) {
        return response.data.data.audit_logs
      } else {
        throw new Error(response.data.message || 'Failed to get audit log')
      }
    } catch (error) {
      console.error('Error getting audit log:', error)
      throw error
    }
  }

  const getTransactions = async (registerId, filters = {}) => {
    try {
      const response = await api.get(`/registers/${registerId}/data/transactions`, {
        params: filters
      })

      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to get transactions')
      }
    } catch (error) {
      console.error('Error getting transactions:', error)
      throw error
    }
  }

  const exportAuditLog = async (registerId, format = 'csv', filters = {}) => {
    try {
      const response = await api.get(`/registers/${registerId}/data/export-audit-log`, {
        params: { ...filters, format },
        responseType: 'blob'
      })

      // Create download link
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `register_${registerId}_audit_log.${format}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      return true
    } catch (error) {
      console.error('Error exporting audit log:', error)
      throw error
    }
  }

  const validateCash = async (denominations) => {
    try {
      const response = await api.post('/cash/validate', {
        denominations: denominations
      })

      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to validate cash')
      }
    } catch (error) {
      console.error('Error validating cash:', error)
      throw error
    }
  }

  const getValidDenominations = async () => {
    try {
      const response = await api.get('/cash/denominations')
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to get valid denominations')
      }
    } catch (error) {
      console.error('Error getting valid denominations:', error)
      throw error
    }
  }

  const setCurrentRegister = (register) => {
    currentRegister.value = register
  }

  const clearCurrentRegister = () => {
    currentRegister.value = null
  }

  const resetOnLogout = () => {
    registers.value = []
    loading.value = false
    currentRegister.value = null
    registersWithTerminals.value = []
    terminalStatusLoading.value = false
    terminalOpsInflightCount = 0
    loadOverviewInflight = null
    overviewLastFetchedAt = 0
    loadRegistersInflight = null
    registersLastFetchedAt = 0
    loadCurrentRegisterInflight = null
    currentRegisterLastFetchedAt = 0
    loadRwtInflight = null
    rwtLastFetchedAt = 0
    terminalStatusInflightByRegisterId.clear()
  }

  const refreshRegisters = async () => {
    await loadRegisters()
  }

  /* ==================== TERMINAL MANAGEMENT ==================== */

  /**
   * Load registers with their terminal assignments for the current location
   */
  let loadRwtInflight = null
  let rwtLastFetchedAt = 0
  const loadRegistersWithTerminals = async ({ force = false } = {}) => {
    if (!locationStore.getCurrentLocation?.id) {
      throw new Error('No location selected')
    }

    if (!force && loadRwtInflight) return loadRwtInflight
    const now = Date.now()
    if (!force && now - rwtLastFetchedAt < 30000 && registersWithTerminals.value.length > 0) {
      return registersWithTerminals.value
    }

    terminalOpsInflightCount += 1
    syncTerminalOpsLoading()
    try {
      loadRwtInflight = api.get('/payments/registers-with-terminals')
      const response = await loadRwtInflight
      if (response.data.success) {
        registersWithTerminals.value = response.data.registers_with_terminals
        rwtLastFetchedAt = Date.now()
        return registersWithTerminals.value
      } else {
        throw new Error(response.data.message || 'Failed to load registers with terminals')
      }
    } catch (error) {
      console.error('Error loading registers with terminals:', error)
      throw error
    } finally {
      terminalOpsInflightCount = Math.max(0, terminalOpsInflightCount - 1)
      syncTerminalOpsLoading()
      loadRwtInflight = null
    }
  }

  /**
   * Get terminal status for a specific register
   */
  const terminalStatusInflightByRegisterId = new Map()
  const getTerminalStatusForRegister = async (registerId, { force = false } = {}) => {
    if (!registerId) {
      throw new Error('No register ID provided')
    }

    if (!force) {
      const existing = terminalStatusInflightByRegisterId.get(registerId)
      if (existing) return existing
    }

    terminalOpsInflightCount += 1
    syncTerminalOpsLoading()

    const request = (async () => {
      try {
        const response = await api.get(`/payments/register/${registerId}/terminal-status`)

        if (response.data.success) {
          return response.data
        }
        throw new Error(response.data.message || 'Failed to get terminal status')
      } catch (error) {
        console.error('Error getting terminal status for register:', error)
        throw error
      } finally {
        terminalOpsInflightCount = Math.max(0, terminalOpsInflightCount - 1)
        syncTerminalOpsLoading()
        terminalStatusInflightByRegisterId.delete(registerId)
      }
    })()

    // Only store for dedupe when not forced.
    if (!force) {
      terminalStatusInflightByRegisterId.set(registerId, request)
    }

    return request
  }

  /**
   * Assign a terminal to a register
   */
  const assignTerminalToRegister = async (registerId, terminalId) => {
    if (!registerId || !terminalId) {
      throw new Error('Register ID and Terminal ID are required')
    }

    console.log('Store: Assigning terminal to register:', { registerId, terminalId, payload: { terminal_id: terminalId } })

    try {
      const response = await api.post(`/registers/${registerId}/terminal/assign`, {
        terminal_id: terminalId
      })

      if (response.data.success) {
        // Refresh the registers with terminals data
        await loadRegistersWithTerminals()
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to assign terminal to register')
      }
    } catch (error) {
      console.error('Error assigning terminal to register:', error)
      throw error
    }
  }

  /**
   * Unassign terminal from a register
   */
  const unassignTerminalFromRegister = async (registerId) => {
    if (!registerId) {
      throw new Error('Register ID is required')
    }

    try {
      const response = await api.delete(`/registers/${registerId}/terminal/unassign`)

      if (response.data.success) {
        // Refresh the registers with terminals data
        await loadRegistersWithTerminals()
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to unassign terminal from register')
      }
    } catch (error) {
      console.error('Error unassigning terminal from register:', error)
      throw error
    }
  }

  /**
   * Check if current register can process card payments
   */
  const checkCurrentRegisterCardPaymentCapability = async () => {
    if (!currentRegister.value) {
      return false
    }

    try {
      const terminalStatus = await getTerminalStatusForRegister(currentRegister.value.id)
      return terminalStatus?.can_process_cards || false
    } catch (error) {
      console.error('Error checking register card payment capability:', error)
      return false
    }
  }

  return {
    // State
    registers,
    loading,
    currentRegister,
    registersWithTerminals,
    terminalStatusLoading,

    // Getters
    getRegistersByLocation,
    getAvailableRegisters,
    getOpenRegisters,
    getRegisterById,
    getRegistersWithTerminals,
    getRegistersReadyForCardPayments,
    getCurrentRegisterTerminalStatus,
    getCurrentRegisterCanProcessCards,
    isCurrentRegisterOpen,

    // Actions
    loadRegisters,
    loadOverview,
    loadCurrentUserRegister,
    hydrateCurrentRegisterFromBootstrap,
    hydrateOverviewFromCache,
    createRegister,
    updateRegister,
    deleteRegister,
    openRegister,
    closeRegister,
    assignUserToRegister,
    unassignUserFromRegister,
    getRegisterStatus,
    getPerformanceMetrics,
    getCashStatus,
    recordCashAdjustment,
    getCashBreakdown,
    getAuditLog,
    getTransactions,
    exportAuditLog,
    validateCash,
    getValidDenominations,
    setCurrentRegister,
    clearCurrentRegister,
    resetOnLogout,
    refreshRegisters,

    // Terminal management
    loadRegistersWithTerminals,
    getTerminalStatusForRegister,
    assignTerminalToRegister,
    unassignTerminalFromRegister,
    checkCurrentRegisterCardPaymentCapability
  }
})
